import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api2 from '../axios_Interceptor/api2';
import { apiBaseUrl } from '../connection';

const SESSION = 'fanbird.analytics.session.v1', ANON = 'fanbird.analytics.anon.v1', TOUCH = 'fanbird.analytics.touch.v1';
const id = () => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
};
const validId = (value: unknown) => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
export function analyticsSession(now = Date.now()) {
  let anon = localStorage.getItem(ANON);
  if (!validId(anon)) { anon = id(); localStorage.setItem(ANON, anon); }
  let session: { id: string; last: number } | null = null;
  try { session = JSON.parse(localStorage.getItem(SESSION) || 'null'); } catch { /* Start a new session after damaged storage. */ }
  if (!session || !validId(session.id) || !Number.isFinite(session.last) || now < session.last || now - session.last >= 30 * 60000)
    session = { id: id(), last: now };
  session.last = now; localStorage.setItem(SESSION, JSON.stringify(session));
  return { anon, session: session.id };
}

export function AnalyticsNavigation() {
  const location = useLocation();
  const initial = useRef(true);
  useEffect(() => {
    if (/^\/admin|^\/broadcast|^\/payment-review/.test(location.pathname)) return;
    try {
      const params = new URLSearchParams(location.search);
      const source = params.get('utm_source');
      let referrer = '';
      try { const url = new URL(document.referrer); if (initial.current && url.origin !== window.location.origin) referrer = url.hostname; } catch { /* No referrer is unknown, never guessed. */ }
      initial.current = false;
      if (source || params.get('fb_campaign') || referrer) sessionStorage.setItem(TOUCH, JSON.stringify({
        source: source || referrer, medium: params.get('utm_medium') || (referrer ? 'referral' : ''),
        campaign_id: params.get('fb_campaign') || '', message_token: params.get('fb_message') || '', at: Date.now(), path: location.pathname,
      }));
    } catch { /* Analytics must not block navigation when storage is disabled. */ }
  }, [location.pathname, location.search]);
  return null;
}

export function trackLiveEvent(name: 'live_enter' | 'product_exposure' | 'product_detail', live: number, product?: number, captureTouch = false) {
  if (!live || document.visibilityState === 'hidden') return;
  try {
    const session = analyticsSession();
    let touch: any = null;
    if (captureTouch) {
      const params = new URLSearchParams(window.location.search);
      const pending = JSON.parse(sessionStorage.getItem(TOUCH) || 'null');
      if (pending && Date.now() - pending.at < 30 * 60000) touch = pending;
      else if (params.has('utm_source') || params.has('fb_campaign')) touch = { source: params.get('utm_source'), medium: params.get('utm_medium'), campaign_id: params.get('fb_campaign'), message_token: params.get('fb_message') };
      sessionStorage.removeItem(TOUCH);
    }
    void api2.post(`${apiBaseUrl()}analytics/events`, { id: id(), name, live_seq: live, product_seq: product,
      ...session, ...(touch || {}) }, { timeout: 5000 }).catch(() => undefined);
  } catch { /* Analytics is independent of ordering, login and playback. */ }
}

export function useLiveAnalytics(live: { seq: number; live_status: string; integration_test?: boolean } | undefined, loggedIn: boolean, disabled = false) {
  const entered = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!live || disabled || live.integration_test || live.live_status === '리허설') return;
    const record = () => {
      if (document.visibilityState !== 'visible') return;
      const first = entered.current !== live.seq;
      trackLiveEvent('live_enter', live.seq, undefined, first);
      entered.current = live.seq;
    };
    record();
    document.addEventListener('visibilitychange', record);
    return () => document.removeEventListener('visibilitychange', record);
  }, [live?.seq, live?.live_status, loggedIn, disabled, live?.integration_test]);
}
